/**
 * Hooks server-state Impersonation read-only "View as user" (US-AE11.F3-0039-S03 · @UC9102) — bọc
 * TanStack Query.
 *  - useImpersonationSession(): phiên active hiện tại (banner render). refetchInterval để tự phát hiện
 *    hết hạn server-side (lazy expiry) → banner tự ẩn. retry:false.
 *  - useStartImpersonation(): mutating → LƯU token in-memory (context) + invalidate active (banner + panel
 *    xuất hiện). "View as user" hoạt động thật: panel đọc màn hình user bằng token này.
 *  - useEndImpersonation(): mutating → XOÁ token (panel ẩn, không đọc tiếp) + invalidate active (banner ẩn).
 *  - useImpersonatedProfile(): GET /v1/me bằng token impersonation → hồ sơ target user (read-only panel).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys.js";
import type { StartImpersonationBody } from "@goppy/contracts";
import { getToken } from "@/lib/auth.js";
import {
  endImpersonation,
  getActiveImpersonation,
  getImpersonatedProfile,
  startImpersonation,
} from "./api.js";
import { useImpersonationToken } from "./impersonation-token-context.js";

/** Poll active phiên mỗi 30s để tự phát hiện hết hạn (server lazy-expire → session null → banner ẩn). */
const ACTIVE_POLL_INTERVAL_MS = 30_000;

/**
 * Query phiên impersonation active. Banner/View mount ở App root nên hook này chạy trên MỌI màn —
 * PHẢI gate bằng `enabled: có-admin-token` để KHÔNG bắn GET /admin/impersonation/active khi chưa
 * đăng nhập (kể cả /login) → tránh 401 + console.error toàn cục (US-AE.F9-0100 BUG-2).
 * Caller vẫn có thể tắt thêm qua tham số `enabled`.
 */
export function useImpersonationSession(enabled = true) {
  return useQuery({
    queryKey: queryKeys.impersonation.active(),
    queryFn: getActiveImpersonation,
    retry: false,
    enabled: enabled && Boolean(getToken()),
    refetchInterval: ACTIVE_POLL_INTERVAL_MS,
  });
}

export function useStartImpersonation(userId: string) {
  const queryClient = useQueryClient();
  const { setToken } = useImpersonationToken();
  return useMutation({
    mutationFn: (body: StartImpersonationBody) => startImpersonation(userId, body),
    onSuccess: (session) => {
      // LƯU token in-memory (context) để panel read-only đọc màn hình user BẰNG token này (Bearer).
      if (session.token) setToken(session.token);
      void queryClient.invalidateQueries({ queryKey: queryKeys.impersonation.all });
    },
  });
}

export function useEndImpersonation() {
  const queryClient = useQueryClient();
  const { clearToken } = useImpersonationToken();
  return useMutation({
    mutationFn: (sessionId: string) => endImpersonation(sessionId),
    onSuccess: () => {
      // XOÁ token: panel read-only ẩn ngay, không đọc tiếp (backend cũng thu hồi qua sid-active check).
      clearToken();
      void queryClient.invalidateQueries({ queryKey: queryKeys.impersonation.all });
    },
  });
}

/**
 * Hồ sơ target user đọc BẰNG token impersonation (GET /v1/me) — chứng minh token hoạt động end-to-end.
 * enabled chỉ khi có token (đang trong phiên). retry:false (403 khi phiên thu hồi → panel hiện lỗi, không
 * spam retry). Token qua closure; queryKey ổn định trong 1 phiên.
 */
export function useImpersonatedProfile(token: string | null) {
  return useQuery({
    queryKey: queryKeys.impersonation.profile(),
    queryFn: () => getImpersonatedProfile(token as string),
    enabled: token !== null,
    retry: false,
    staleTime: 0,
  });
}
